import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Sparkles,
    Target,
    Send,
    Plus,
    Loader2,
    X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { CommandCenterProvider, useCommandCenter } from '../contexts/CommandCenterContext';
import { DATE_RANGE_LABELS } from '../utils/helpers';
import AdvisorMessage from '../components/command-center/AdvisorMessage';
import {
    sendAdvisorMessage,
    createUserMessage,
    createAssistantMessage,
    executeAction,
    undoAction,
    summarizeHistory,
    GOAL_PRESETS
} from '../services/advisorChatService';

/**
 * AdvisorDeepDive - Full conversation page for AI Advisor
 * Shows complete chat history with inline data cards and action management
 */
const AdvisorDeepDiveContent = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { selectedBrand } = useApp();

    // Get campaign data and advisor state from CommandCenter context
    const {
        campaigns,
        insights,
        aggregateMetrics,
        previousAggregateMetrics,
        aggregateTrends,
        previousPeriodData,
        dateRangePreset,
        isLoading: contextLoading,
        // Shared advisor state from context
        advisorMessages,
        advisorGoal,
        advisorChatId,
        advisorLoading,
        updateAdvisorMessages,
        updateAdvisorGoal,
        startNewAdvisorChat,
        setAdvisorChatId
    } = useCommandCenter();

    // Local UI state only
    const [inputValue, setInputValue] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [actionPending, setActionPending] = useState(null);
    const [showGoalSelector, setShowGoalSelector] = useState(false);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [advisorMessages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Helper to calculate trend percentage
    const calculateTrendPercent = (current, previous) => {
        if (!previous || previous === 0) return null;
        return ((current - previous) / previous) * 100;
    };

    // Build context for AI using real data from CommandCenter
    const buildContext = () => {
        const safeMetrics = aggregateMetrics || { spend: 0, revenue: 0, impressions: 0, clicks: 0, conversions: 0 };
        const safePrevMetrics = previousAggregateMetrics || {};
        const activeCampaigns = campaigns.filter(c => c.status?.toUpperCase() === 'ACTIVE');
        const overallROAS = safeMetrics.spend > 0 ? (safeMetrics.revenue || 0) / safeMetrics.spend : 0;
        const prevROAS = safePrevMetrics.spend > 0 ? (safePrevMetrics.revenue || 0) / safePrevMetrics.spend : 0;

        return {
            dateRange: DATE_RANGE_LABELS[dateRangePreset] || 'this period',
            metrics: safeMetrics,
            roas: overallROAS,
            // Include trends so AI can understand if things are improving or declining
            trends: {
                spend: aggregateTrends?.spend || calculateTrendPercent(safeMetrics.spend, safePrevMetrics.spend),
                revenue: aggregateTrends?.revenue || calculateTrendPercent(safeMetrics.revenue, safePrevMetrics.revenue),
                roas: aggregateTrends?.roas || calculateTrendPercent(overallROAS, prevROAS),
                impressions: aggregateTrends?.impressions || calculateTrendPercent(safeMetrics.impressions, safePrevMetrics.impressions),
                clicks: aggregateTrends?.clicks || calculateTrendPercent(safeMetrics.clicks, safePrevMetrics.clicks),
                conversions: aggregateTrends?.conversions || calculateTrendPercent(safeMetrics.conversions, safePrevMetrics.conversions)
            },
            previousPeriodMetrics: safePrevMetrics,
            campaigns: campaigns.slice(0, 10).map(c => {
                // Metrics are in currentMetrics or insights.data[0]
                const metrics = c.currentMetrics || c.insights?.data?.[0] || {};
                const prevMetrics = previousPeriodData?.[c.id] || {};

                // Calculate per-campaign trends
                const campaignRoas = metrics.roas || (metrics.spend > 0 ? (metrics.revenue || 0) / metrics.spend : 0);
                const prevCampaignRoas = prevMetrics.roas || (prevMetrics.spend > 0 ? (prevMetrics.revenue || 0) / prevMetrics.spend : 0);

                return {
                    id: c.id,
                    name: c.name,
                    status: c.status,
                    objective: c.objective,
                    spend: metrics.spend || 0,
                    revenue: metrics.revenue || 0,
                    roas: campaignRoas,
                    impressions: metrics.impressions || 0,
                    clicks: metrics.clicks || 0,
                    conversions: metrics.conversions || 0,
                    ctr: metrics.ctr || 0,
                    cpc: metrics.cpc || 0,
                    cpa: metrics.cpa || 0,
                    dailyBudget: c.dailyBudget || c.budget || c.daily_budget,
                    // Per-campaign trends
                    trends: {
                        spend: calculateTrendPercent(metrics.spend, prevMetrics.spend),
                        revenue: calculateTrendPercent(metrics.revenue, prevMetrics.revenue),
                        roas: calculateTrendPercent(campaignRoas, prevCampaignRoas),
                        conversions: calculateTrendPercent(metrics.conversions, prevMetrics.conversions)
                    }
                };
            }),
            insights: insights.slice(0, 5),
            totalCampaigns: campaigns.length,
            activeCampaigns: activeCampaigns.length
        };
    };

    // Handle goal selection
    const handleGoalSelect = async (goal) => {
        updateAdvisorGoal(goal);
        setShowGoalSelector(false);
        setSending(true);
        setError(null);

        try {
            const response = await sendAdvisorMessage({
                message: `I want to ${goal.objective.toLowerCase()}. What should I focus on?`,
                conversationHistory: [],
                context: buildContext(),
                userGoal: goal
            });

            const userMsg = createUserMessage(`I want to ${goal.objective.toLowerCase()}.`);
            const assistantMsg = createAssistantMessage(response);

            updateAdvisorMessages([userMsg, assistantMsg]);
        } catch (err) {
            console.error('Failed to start conversation:', err);
            setError('Failed to connect to advisor. Try again.');
        } finally {
            setSending(false);
        }
    };

    // Handle sending a message
    const handleSend = async () => {
        if (!inputValue.trim() || sending) return;

        const userMsg = createUserMessage(inputValue.trim());
        const newMessages = [...advisorMessages, userMsg];
        updateAdvisorMessages(newMessages);
        setInputValue('');
        setSending(true);
        setError(null);

        try {
            // Summarize history if too long
            const historyForAI = summarizeHistory(advisorMessages);

            const response = await sendAdvisorMessage({
                message: userMsg.content,
                conversationHistory: historyForAI.map(m => ({
                    role: m.role,
                    content: m.content
                })),
                context: buildContext(),
                userGoal: advisorGoal
            });

            const assistantMsg = createAssistantMessage(response);
            updateAdvisorMessages([...newMessages, assistantMsg]);
        } catch (err) {
            console.error('Failed to send message:', err);
            setError('Failed to send message. Try again.');
        } finally {
            setSending(false);
        }
    };

    // Handle suggestion click
    const handleSuggestionClick = (suggestion) => {
        setInputValue(suggestion);
        inputRef.current?.focus();
    };

    // Handle action approval
    const handleApproveAction = async (action) => {
        setActionPending(action.campaignId);
        try {
            const result = await executeAction(action);

            // Update message with executed action
            const updatedMessages = advisorMessages.map(msg => {
                if (msg.proposedAction?.campaignId === action.campaignId) {
                    return {
                        ...msg,
                        proposedAction: null,
                        executedAction: {
                            ...action,
                            undoable: true,
                            undoAction: result.undoAction
                        }
                    };
                }
                return msg;
            });
            updateAdvisorMessages(updatedMessages);
        } catch (err) {
            console.error('Failed to execute action:', err);
            setError(`Action failed: ${err.message}`);
        } finally {
            setActionPending(null);
        }
    };

    // Handle action modification (open input with suggestion)
    const handleModifyAction = (action) => {
        setInputValue(`I'd like to modify this action: ${action.description}. `);
        inputRef.current?.focus();
    };

    // Handle action skip
    const handleSkipAction = (action) => {
        const updatedMessages = advisorMessages.map(msg => {
            if (msg.proposedAction?.campaignId === action.campaignId) {
                return { ...msg, proposedAction: null };
            }
            return msg;
        });
        updateAdvisorMessages(updatedMessages);
    };

    // Handle undo action
    const handleUndoAction = async (action) => {
        if (!action.undoAction) return;

        setActionPending(action.campaignId);
        try {
            await undoAction(action.undoAction);

            // Mark action as undone
            const updatedMessages = advisorMessages.map(msg => {
                if (msg.executedAction?.campaignId === action.campaignId) {
                    return {
                        ...msg,
                        executedAction: {
                            ...msg.executedAction,
                            undoneAt: new Date().toISOString()
                        }
                    };
                }
                return msg;
            });
            updateAdvisorMessages(updatedMessages);
        } catch (err) {
            console.error('Failed to undo action:', err);
            setError(`Undo failed: ${err.message}`);
        } finally {
            setActionPending(null);
        }
    };

    // Start new chat
    const handleNewChat = () => {
        startNewAdvisorChat();
        setShowGoalSelector(false);
    };

    // Handle navigation actions from advisor
    const handleNavigate = (destination, campaignConfig) => {
        // If there's a campaign config, we could pass it via state or query params
        if (campaignConfig) {
            navigate(destination, { state: { campaignConfig } });
        } else {
            navigate(destination);
        }
    };

    // Get latest suggestions
    const latestAssistantMessage = [...advisorMessages].reverse().find(m => m.role === 'assistant');
    const suggestions = latestAssistantMessage?.suggestions || [];

    // Use advisorLoading from context for initial load
    const loading = advisorLoading;

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            background: 'var(--color-background)'
        }}>
            {/* Header */}
            <header style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                padding: 'var(--space-4) var(--space-6)',
                borderBottom: '1px solid var(--color-border)',
                background: 'var(--color-surface)'
            }}>
                <button
                    onClick={() => navigate('/command-center')}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-2)',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--color-text-secondary)',
                        cursor: 'pointer',
                        borderRadius: 'var(--radius-md)'
                    }}
                >
                    <ArrowLeft size={20} />
                </button>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)'
                }}>
                    <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: 'var(--radius-full)',
                        background: 'var(--gradient-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Sparkles size={18} style={{ color: 'white' }} />
                    </div>
                    <div>
                        <h1 style={{
                            margin: 0,
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)'
                        }}>
                            AI Advisor
                        </h1>
                        {selectedBrand && (
                            <span style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)'
                            }}>
                                {selectedBrand.name}
                            </span>
                        )}
                    </div>
                </div>

                <div style={{ flex: 1 }} />

                {/* Goal badge */}
                {advisorGoal && (
                    <button
                        onClick={() => setShowGoalSelector(true)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-2) var(--space-3)',
                            background: 'rgba(124, 58, 237, 0.1)',
                            border: '1px solid rgba(124, 58, 237, 0.2)',
                            borderRadius: 'var(--radius-full)',
                            color: 'var(--color-primary)',
                            fontSize: 'var(--font-size-sm)',
                            cursor: 'pointer'
                        }}
                    >
                        <Target size={14} />
                        {advisorGoal.objective}
                    </button>
                )}

                <button
                    onClick={handleNewChat}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-2) var(--space-3)',
                        background: 'var(--color-surface-elevated)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--color-text-secondary)',
                        fontSize: 'var(--font-size-sm)',
                        cursor: 'pointer'
                    }}
                >
                    <Plus size={14} />
                    New Chat
                </button>
            </header>

            {/* Messages area */}
            <div style={{
                flex: 1,
                overflow: 'auto',
                padding: 'var(--space-6)'
            }}>
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto'
                }}>
                    {/* Loading state */}
                    {loading && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 'var(--space-8)',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            <Loader2 size={24} className="spin" />
                        </div>
                    )}

                    {/* No goal set and no messages - show goal selector */}
                    {!loading && !advisorGoal && !showGoalSelector && advisorMessages.length === 0 && (
                        <GoalSelectorCard onSelect={handleGoalSelect} loading={sending} />
                    )}

                    {/* Goal selector modal */}
                    {showGoalSelector && (
                        <GoalSelectorModal
                            onSelect={(goal) => {
                                updateAdvisorGoal(goal);
                                setShowGoalSelector(false);
                            }}
                            onClose={() => setShowGoalSelector(false)}
                            currentGoal={advisorGoal}
                        />
                    )}

                    {/* Messages */}
                    {advisorMessages.map((message) => (
                        <AdvisorMessage
                            key={message.id}
                            message={message}
                            onApproveAction={handleApproveAction}
                            onModifyAction={handleModifyAction}
                            onSkipAction={handleSkipAction}
                            onUndoAction={handleUndoAction}
                            onNavigate={handleNavigate}
                            isActionPending={actionPending === message.proposedAction?.campaignId ||
                                actionPending === message.executedAction?.campaignId}
                        />
                    ))}

                    {/* Sending indicator */}
                    {sending && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                            padding: 'var(--space-4)'
                        }}>
                            <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: 'var(--radius-full)',
                                background: 'var(--gradient-primary)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Sparkles size={16} style={{ color: 'white' }} />
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                color: 'var(--color-text-tertiary)',
                                fontSize: 'var(--font-size-sm)'
                            }}>
                                <Loader2 size={14} className="spin" />
                                Thinking...
                            </div>
                        </div>
                    )}

                    {/* Error message */}
                    {error && (
                        <div style={{
                            padding: 'var(--space-3) var(--space-4)',
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-error)'
                        }}>
                            {error}
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input area - show when there's a goal OR messages already exist */}
            {(advisorGoal || advisorMessages.length > 0) && (
                <div style={{
                    borderTop: '1px solid var(--color-border)',
                    background: 'var(--color-surface)',
                    padding: 'var(--space-4) var(--space-6)'
                }}>
                    <div style={{
                        maxWidth: '800px',
                        margin: '0 auto'
                    }}>
                        {/* Suggestions */}
                        {suggestions.length > 0 && (
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 'var(--space-2)',
                                marginBottom: 'var(--space-3)'
                            }}>
                                {suggestions.map((suggestion, i) => (
                                    <button
                                        key={i}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        style={{
                                            padding: 'var(--space-2) var(--space-3)',
                                            background: 'var(--color-surface-elevated)',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: 'var(--radius-full)',
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-secondary)',
                                            cursor: 'pointer',
                                            transition: 'all var(--transition-fast)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--color-primary)';
                                            e.currentTarget.style.color = 'var(--color-primary)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--color-border)';
                                            e.currentTarget.style.color = 'var(--color-text-secondary)';
                                        }}
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Input */}
                        <div style={{
                            display: 'flex',
                            gap: 'var(--space-3)'
                        }}>
                            <input
                                ref={inputRef}
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                                placeholder="Ask the advisor anything..."
                                disabled={sending}
                                style={{
                                    flex: 1,
                                    padding: 'var(--space-3) var(--space-4)',
                                    background: 'var(--color-surface-elevated)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-lg)',
                                    fontSize: 'var(--font-size-md)',
                                    color: 'var(--color-text-primary)',
                                    outline: 'none'
                                }}
                            />
                            <button
                                onClick={handleSend}
                                disabled={sending || !inputValue.trim()}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '48px',
                                    height: '48px',
                                    background: 'var(--color-primary)',
                                    border: 'none',
                                    borderRadius: 'var(--radius-lg)',
                                    color: 'white',
                                    cursor: sending || !inputValue.trim() ? 'not-allowed' : 'pointer',
                                    opacity: sending || !inputValue.trim() ? 0.5 : 1
                                }}
                            >
                                {sending ? (
                                    <Loader2 size={20} className="spin" />
                                ) : (
                                    <Send size={20} />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * GoalSelectorCard - Initial goal selection
 */
const GoalSelectorCard = ({ onSelect, loading }) => (
    <div style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-6)',
        textAlign: 'center',
        maxWidth: '500px',
        margin: '0 auto'
    }}>
        <div style={{
            width: '64px',
            height: '64px',
            borderRadius: 'var(--radius-full)',
            background: 'var(--gradient-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto var(--space-4)'
        }}>
            <Sparkles size={32} style={{ color: 'white' }} />
        </div>

        <h2 style={{
            margin: '0 0 var(--space-2) 0',
            fontSize: 'var(--font-size-xl)',
            fontWeight: 600,
            color: 'var(--color-text-primary)'
        }}>
            What's your goal?
        </h2>

        <p style={{
            margin: '0 0 var(--space-4) 0',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.6
        }}>
            I'll tailor my analysis and recommendations to help you achieve it.
        </p>

        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 'var(--space-2)'
        }}>
            {GOAL_PRESETS.map((goal) => (
                <button
                    key={goal.id}
                    onClick={() => onSelect(goal)}
                    disabled={loading}
                    style={{
                        padding: 'var(--space-3) var(--space-4)',
                        background: 'var(--color-surface-elevated)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-primary)',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.5 : 1,
                        transition: 'all var(--transition-fast)'
                    }}
                    onMouseEnter={(e) => {
                        if (!loading) {
                            e.currentTarget.style.borderColor = 'var(--color-primary)';
                            e.currentTarget.style.background = 'rgba(124, 58, 237, 0.1)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--color-border)';
                        e.currentTarget.style.background = 'var(--color-surface-elevated)';
                    }}
                >
                    {goal.label}
                </button>
            ))}
        </div>

        {loading && (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                marginTop: 'var(--space-4)',
                color: 'var(--color-text-tertiary)',
                fontSize: 'var(--font-size-sm)'
            }}>
                <Loader2 size={16} className="spin" />
                Starting conversation...
            </div>
        )}
    </div>
);

/**
 * GoalSelectorModal - Modal for changing goal
 */
const GoalSelectorModal = ({ onSelect, onClose, currentGoal }) => (
    <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    }}>
        <div style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-6)',
            maxWidth: '400px',
            width: '90%'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-4)'
            }}>
                <h3 style={{
                    margin: 0,
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)'
                }}>
                    Change Goal
                </h3>
                <button
                    onClick={onClose}
                    style={{
                        padding: 'var(--space-1)',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--color-text-tertiary)',
                        cursor: 'pointer'
                    }}
                >
                    <X size={20} />
                </button>
            </div>

            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)'
            }}>
                {GOAL_PRESETS.map((goal) => (
                    <button
                        key={goal.id}
                        onClick={() => onSelect(goal)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                            padding: 'var(--space-3) var(--space-4)',
                            background: currentGoal?.id === goal.id
                                ? 'rgba(124, 58, 237, 0.1)'
                                : 'var(--color-surface-elevated)',
                            border: `1px solid ${currentGoal?.id === goal.id
                                ? 'var(--color-primary)'
                                : 'var(--color-border)'}`,
                            borderRadius: 'var(--radius-lg)',
                            cursor: 'pointer',
                            textAlign: 'left'
                        }}
                    >
                        <Target size={16} style={{
                            color: currentGoal?.id === goal.id
                                ? 'var(--color-primary)'
                                : 'var(--color-text-tertiary)'
                        }} />
                        <div>
                            <div style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                color: 'var(--color-text-primary)'
                            }}>
                                {goal.label}
                            </div>
                            <div style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)'
                            }}>
                                {goal.description}
                            </div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    </div>
);

// Wrapper component with CommandCenterProvider
const AdvisorDeepDive = () => {
    return (
        <CommandCenterProvider>
            <AdvisorDeepDiveContent />
        </CommandCenterProvider>
    );
};

export default AdvisorDeepDive;
